import {
  AppBar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SvgIcon,
  Toolbar,
  Typography,
  Drawer,
} from '@material-ui/core/';
import MenuIcon from '@material-ui/icons/Menu';
import classNames from 'classnames';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppConfig } from '~/hooks/useAppConfig';
import useActiveOrdersCount from '~/models/Orders/useActiveOrdersCount';
import { switchLocale } from '~/store/legacy/actions/appActions';
import { selectMeals } from '~/store/legacy/selector/shoppingCartSelectors';
import { selectIsUserLoggedIn, selectUser } from '~/store/legacy/selector/userSelectors';
import { intercom } from '~/third_party/Intercom';
import EventEmitter from '~/utils/eventEmitter';
import chowbusLogo from '../assets/chowbus_logo.png';
import chowbusLogoText from '../assets/chowbus_logo_text.png';
import chowbusLogoTextWhite from '../assets/chowbus_logo_text_white.png';
import chowbusLogoWhite from '../assets/chowbus_logo_white.png';
import shoppingBag from '../assets/paper-bag.svg';
import useStyles from './AppToolBar.style';
import ShoppingCartDropdown from './cart/ShoppingCartDropdown';

const UserControls = ({ variant }) => {
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  if (isUserLoggedIn) {
    return (
      <Grid container justify='flex-end' style={{ marginRight: 10 }}>
        <LoggedInUserMenu variant={variant} />
      </Grid>
    );
  } else {
    return (
      <>
        <SignInButtons variant={variant} />
        <MobileLoggedOutMenu variant={variant} />
      </>
    );
  }
};

const SignInButtons = ({ variant }) => {
  const classes = useStyles();
  return (
    <Grid container justify='flex-end' className={classes.signInButtons}>
      <Link href='/sign-in'>
        <Button
          variant='outlined'
          size='small'
          color={variant}
          classes={{ outlined: classes.menuButton, outlinedSecondary: classes.outlinedSecondary }}
          style={{ marginRight: 10 }}>
          Log In
        </Button>
      </Link>
      <Link href='/sign-up'>
        <Button
          variant='contained'
          size='small'
          color={variant}
          classes={{
            contained: classNames(classes.menuButton, classes.siginUpButton),
            containedSecondary: classes.containedSecondary,
          }}>
          Sign up
        </Button>
      </Link>
    </Grid>
  );
};

const MobileLoggedOutMenu = ({ variant }) => {
  const classes = useStyles();
  const open = true;
  return (
    <Grid container justify='flex-end' className={classes.mobileLoggedOutMenu}>
      <UserMenuPopup
        anchor={(ref, toggleMenu) => (
          <IconButton
            className={classes.iconButton}
            ref={ref}
            aria-label='Menu'
            onClick={toggleMenu}
            aria-owns={open ? 'menu-list-grow' : null}
            aria-haspopup='true'>
            <MenuIcon
              className={classNames({
                [classes.shoppingCartIconSecondary]: variant === 'secondary',
              })}
            />
          </IconButton>
        )}
      />
    </Grid>
  );
};

const LoggedInUserMenu = (props) => {
  const classes = useStyles();
  const { variant } = props;
  const user = useSelector(selectUser);
  const { activeOrderCount } = useActiveOrdersCount();
  return (
    <div className={classes.userLink}>
      <UserMenuPopup
        anchor={(ref, toggleMenu) => (
          <Badge
            badgeContent={<Typography variant='caption'>{activeOrderCount}</Typography>}
            classes={{
              badge: classNames(classes.orderBadge, {
                [classes.badgeHidden]: activeOrderCount === 0,
              }),
            }}>
            <Button
              ref={ref}
              className={classNames({
                [classes.userBadgeSecondaryButton]: variant === 'secondary',
              })}
              onClick={toggleMenu}>
              {user ? `Hi, ${user.first_name}` : 'Hi, there'}
            </Button>
          </Badge>
        )}
      />
    </div>
  );
};

/**
 * This is the thing that handles the actual popup. It takes a
 * render prop called `anchor`. That render prop is a function that gets a `ref`
 * and a `toggleMenu` function. Use it like this:
```
  <UserMenuPopup anchor={(ref, toggleMenu) => (
    <MyComponent ref={ref} onClick={toggleMenu}
  )} />
```
 */
const UserMenuPopup = (props) => {
  const { anchor } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handlePopoverClose = (e) => {
    if (anchorRef.current && e && anchorRef.current.contains(e.target)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      {anchor(anchorRef, handleToggle)}
      <Popper
        style={{ zIndex: 2 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            data-locator='user-menu'
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
            <ClickAwayListener onClickAway={handlePopoverClose}>
              <Paper elevation={1}>
                <UserMenu closePopper={handlePopoverClose} open={open} />
              </Paper>
            </ClickAwayListener>
          </Grow>
        )}
      </Popper>
    </>
  );
};

const UserMenu = (props) => {
  const { closePopper, open } = props;
  const classes = useStyles();
  const { activeOrderCount } = useActiveOrdersCount();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const { isAlipay } = useAppConfig();
  const openIntercom = () => {
    intercom.show();
    closePopper();
  };
  const links = [];
  if (isUserLoggedIn) {
    links.push(
      <Link href='/my-account' key='account'>
        <a className={classes.menuItemAnchor}>
          <MenuItem onClick={closePopper}>Account Settings</MenuItem>
        </a>
      </Link>
    );
    links.push(
      <Link href='/orders' key='orders'>
        <a style={{ textDecoration: 'none', color: 'inherit' }}>
          <MenuItem className={classes.userMenuItem} onClick={closePopper}>
            <Badge
              badgeContent={activeOrderCount}
              classes={{
                badge: classNames(classes.orderBadge, classes.orderBadgeItem),
              }}>
              My Orders
            </Badge>
          </MenuItem>
        </a>
      </Link>
    );
  } else {
    links.push(
      <Link href='/sign-in' key='sign-in'>
        <a className={classes.menuItemAnchor}>
          <MenuItem onClick={closePopper}>Sign In</MenuItem>
        </a>
      </Link>
    );
    links.push(
      <Link href='/sign-up' key='sign-up'>
        <a className={classes.menuItemAnchor}>
          <MenuItem onClick={closePopper}>Sign Up</MenuItem>
        </a>
      </Link>
    );
  }
  links.push(
    <a href='/driver' key='driver' className={classes.menuItemAnchor}>
      <MenuItem onClick={closePopper}>Become A Driver</MenuItem>
    </a>
  );
  if (!isAlipay) {
    links.push(
      <MenuItem key='chat' onClick={openIntercom}>
        Chat with us
      </MenuItem>
    );
  }


  return <MenuList autoFocusItem={open}>{links}</MenuList>;
};

const LanguageSwitchButton = ({ variant }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { locale } = useSelector((state) => {
    return state.app;
  });
  let onToggleDisplayLanguage = useCallback(() => {
    dispatch(switchLocale(locale === 'zh' ? 'en' : 'zh'));
  }, [locale]);

  return (
    <Grid container justify='center'>
      <Button
        variant='outlined'
        size='small'
        color={variant}
        onClick={onToggleDisplayLanguage}
        classes={{
          outlined: classes.languageSwitchButton,
          outlinedSecondary: classes.outlinedSecondary,
        }}
        style={{ marginRight: 0 }}>
        {locale === 'zh' ? 'EN' : '中'}
      </Button>
    </Grid>
  );
};

const ShoppingCartIconButton = (props) => {
  const { variant } = props;
  const classes = useStyles();
  const items = useSelector(selectMeals);
  const [anchorEl, setAnchorEl] = useState(null);
  const [shoppingDrawerOpen, setShoppingDrawerOpen] = useState(false);
  useEffect(() => {
    if (items && items.length === 0) {
      setShoppingDrawerOpen(false);
    }
  }, [items]);
  const handlePopoverOpen = (event) => {
    if (items.length > 0) {
      setShoppingDrawerOpen(true);
    }

  };
  const handlePopoverClose = () => {
    setShoppingDrawerOpen(false);
  };


  const open = Boolean(anchorEl);
  return (
    <div
      onMouseEnter={handlePopoverOpen}
    // onMouseLeave={handlePopoverClose}
    >
      <Link href='/checkout'>
        <div className={classes.shoppingCartLink} onClick={handlePopoverClose}>
          <Badge
            badgeContent={<Typography variant='caption'>{items.length}</Typography>}
            classes={{
              badge: classNames(classes.shoppingBagBadge, {
                [classes.badgeHidden]: items.length === 0,
              }),
            }}>
            <IconButton className={classNames(classes.iconButton)}>
              <SvgIcon
                className={classNames({
                  [classes.shoppingCartIconSecondary]: variant === 'secondary',
                })}
                viewBox='0 0 30 32'
                component={shoppingBag}
              />
            </IconButton>
          </Badge>
        </div>
      </Link>
      {items.length > 0 && (
        <Drawer anchor={'right'} open={shoppingDrawerOpen} onClose={handlePopoverClose} >
          <ShoppingCartDropdown />
        </Drawer>
      )
      }
    </div >
  );
};

const AppLogo = (props) => {
  const { mobileImage, desktopImage } = props;
  const classes = useStyles();
  return (
    <>
      <Box display={{ xs: 'inline', sm: 'none' }}>
        <img className={classes.logoImage} src={mobileImage} alt='logo' loading='lazy' />
      </Box>
      <Box display={{ xs: 'none', sm: 'inline' }}>
        <img className={classes.logoImage} src={desktopImage} alt='logo' loading='lazy' />
      </Box>
    </>
  );
};

const AppToolBar = (props) => {
  const {
    variant = 'primary',
    position = 'fixed',
    toolBarClass = '',
    children,
    showLocalSwitchor = false,
  } = props;
  const classes = useStyles();
  const [elements, setElements] = useState({});
  const showElement = (id, element) => {
    setElements((elements) => {
      return {
        ...elements,
        ...{
          [id]: element,
        },
      };
    });
  };

  const hideElement = (id) => {
    setElements((elements) => {
      return {
        ...elements,
        ...{
          [id]: null,
        },
      };
    });
  };

  useEffect(() => {
    EventEmitter.on('showElement', showElement);
    EventEmitter.on('hideElement', hideElement);

    return () => {
      EventEmitter.off('showElement');
      EventEmitter.off('hideElement');
    };
  }, []);

  return (
    <AppBar
      id='header'
      className={classNames(classes.appBar, { [classes.whiteBg]: variant === 'primary' })}
      elevation={0}
      position={position}>
      <Toolbar
        classes={{
          root: classNames(classes.toolBar, toolBarClass),
          gutters: classNames(classes.toolBarGutters, {
            [classes.toolBarGuttersSecondary]: variant === 'secondary',
          }),
        }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item>
            <Link href='/'>
              <a>
                {variant === 'primary' ? (
                  <AppLogo mobileImage={chowbusLogo} desktopImage={chowbusLogoText} />
                ) : (
                  <AppLogo mobileImage={chowbusLogoWhite} desktopImage={chowbusLogoTextWhite} />
                )}
              </a>
            </Link>
          </Grid>
          <Grid item style={{ flexGrow: 1 }}>
            {children}
          </Grid>
          <Grid item>
            <UserControls variant={variant} />
          </Grid>
          <Grid item>
            <ShoppingCartIconButton variant={variant} />
          </Grid>
          {showLocalSwitchor && (
            <Grid item>
              <LanguageSwitchButton variant={variant} />
            </Grid>
          )}
        </Grid>
        {elements.category_tabs}
      </Toolbar>
    </AppBar>
  );
};

export default AppToolBar;
